import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RedeemOrderModalElement } from '@shared/dream-components/src';

import type { OrderStatusResponse } from '~/interfaces/order_status';
import { JOIN_WINDOW_MINUTES } from '~/routes/webinars/_utils/isWebinarJoinable';

export type RedemptionState = 'loading' | 'ready' | 'error';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Same behavior as thank-you Continue: closes modal and applies optional post-purchase redirect. */
  onPostPurchaseContinue: () => void;
  onRedeem: (fulfillmentId: string) => void;
  state: RedemptionState;
  orderStatusData: OrderStatusResponse['order'] | null;
  errorMessage: string | null;
  onAddToCalendar?: (fulfillmentId: string) => void;
  isFree?: boolean;
}

export default function InstantRedemptionModal({
  isOpen,
  onClose,
  onPostPurchaseContinue,
  onRedeem,
  state,
  orderStatusData,
  errorMessage,
  onAddToCalendar,
  isFree = false,
}: Props) {
  const { t } = useTranslation();
  const [isRedeeming, setIsRedeeming] = useState(false);

  const handleClickRedeem = useCallback(
    async (fulfillmentId: string) => {
      setIsRedeeming(true);

      try {
        await onRedeem(fulfillmentId);
      } catch {
        // Error is handled silently - user can retry
      } finally {
        setIsRedeeming(false);
      }
    },
    [onRedeem],
  );

  const translations = {
    'shared.cta.continue': t('shared.cta.continue'),
    'products.purchase_complete': isFree
      ? t('products.free_complete', 'Getting your content ready...')
      : t('products.purchase_complete', 'Purchase complete!'),
    'shared.actions.loading': t('shared.actions.loading', 'Loading'),
    'products.instant_redemption_modal.loading.description': t(
      'products.instant_redemption_modal.loading.description',
      'Preparing your order...',
    ),
    'products.instant_redemption_modal.error.title': t(
      'products.instant_redemption_modal.error.title',
      'Your order is still processing. Please check your email shortly for order details.',
    ),
    'products.instant_redemption_modal.ready.title': isFree
      ? t('products.instant_redemption_modal.ready.free_title', "You're all set!")
      : t('products.instant_redemption_modal.ready.title', 'Your order is ready!'),
    'products.instant_redemption_modal.ready.description': isFree
      ? t(
          'products.instant_redemption_modal.ready.free_description',
          'You can now access your content by clicking the button below or by checking your email',
        )
      : t(
          'products.instant_redemption_modal.ready.description',
          'You can now access your purchase by clicking the button below or by checking your email',
        ),
    'products.instant_redemption_modal.ready.webinar_title': t(
      'products.instant_redemption_modal.ready.webinar_title',
      "You're registered!",
    ),
    'products.instant_redemption_modal.ready.webinar_description': t(
      'products.instant_redemption_modal.ready.webinar_description',
      { minutes: JOIN_WINDOW_MINUTES },
    ),
    'products.instant_redemption_modal.ready.webinar_description_join_now': t(
      'products.instant_redemption_modal.ready.webinar_description_join_now',
    ),
    'products.instant_redemption_modal.close': t('products.instant_redemption_modal.close', 'Close'),
    'products.instant_redemption_modal.ready.download_file': t(
      'products.instant_redemption_modal.ready.download_file',
      'Download file',
    ),
    'products.instant_redemption_modal.ready.access_link': t(
      'products.instant_redemption_modal.ready.access_link',
      'Access link',
    ),
    'products.instant_redemption_modal.ready.join_now': t(
      'products.instant_redemption_modal.ready.join_now',
      'Join now',
    ),
    'products.instant_redemption_modal.ready.redeem': t('products.instant_redemption_modal.ready.redeem', 'Redeem'),
    'products.instant_redemption_modal.ready.book_now': t(
      'products.instant_redemption_modal.ready.book_now',
      'Book now',
    ),
    'products.instant_redemption_modal.ready.add_to_calendar': t(
      'products.instant_redemption_modal.ready.add_to_calendar',
      'Add to calendar',
    ),
    'products.instant_redemption_modal.ready.redeem_available_soon': t(
      'products.instant_redemption_modal.ready.redeem_available_soon',
      { minutes: JOIN_WINDOW_MINUTES },
    ),
  };

  return (
    <RedeemOrderModalElement
      isOpen={isOpen}
      onOpenChange={onClose}
      isRedeeming={isRedeeming}
      onClickRedeem={handleClickRedeem}
      orderStatusState={state}
      orderData={orderStatusData}
      errorMessage={errorMessage}
      onClose={onClose}
      translations={translations}
      onAddToCalendar={onAddToCalendar}
      onPostPurchaseContinue={onPostPurchaseContinue}
      element={{
        type: 'redeemOrderModal',
        attrs: {
          // todo: use website theme colors
          id: `redeem-order-modal-id-${orderStatusData?.id}`,
          width: '400px',
          maxWidth: '400px',
          height: '400px',
          backgroundColor: '#F9FAFB',
          modalOverlayBackgroundColor: '#00000030',
          padding: '24px',
          borderRadius: '8px',
          borderWidth: '1px',
          borderColor: '#E5E7EB',
          borderStyle: 'solid',
          orientation: 'horizontal',
          gap: '16px',
          modalCloseButtonColor: '#030712',
          modalCloseButtonPlacementTop: '24px',
          modalCloseButtonPlacementRight: '24px',
        },
        content: [{ id: '', text: '' }],
      }}
    >
      {null}
    </RedeemOrderModalElement>
  );
}
